import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUpdateStickerMetadata, type MediaSticker } from '../api';

const splitTerms = (value: string) => [
  ...new Set(
    value
      .split(/[\n,]/)
      .map((term) => term.trim())
      .filter(Boolean),
  ),
];

export function StickerMetadataDialog({
  sticker,
  onClose,
}: {
  sticker: MediaSticker | null;
  onClose: () => void;
}) {
  const mutation = useUpdateStickerMetadata();
  const [name, setName] = useState('');
  const [keywords, setKeywords] = useState('');
  const [tags, setTags] = useState('');
  const [style, setStyle] = useState('');
  const [styleName, setStyleName] = useState('');
  const [related, setRelated] = useState('');
  useEffect(() => {
    if (!sticker) return;
    setName(sticker.name);
    setKeywords((sticker.keywords ?? []).join(', '));
    setTags((sticker.tags ?? []).join(', '));
    setStyle(sticker.style ?? '');
    setStyleName(sticker.styleName ?? '');
    setRelated((sticker.relatedCategoryIds ?? []).join(', '));
  }, [sticker]);
  const save = async () => {
    if (!sticker) return;
    try {
      await mutation.mutateAsync({
        stickerId: sticker.stickerId,
        metadata: {
          name: name.trim(),
          keywords: splitTerms(keywords),
          tags: splitTerms(tags),
          style: style.trim() || null,
          styleName: styleName.trim() || null,
          relatedCategoryIds: splitTerms(related),
        },
      });
      toast.success('Sticker metadata updated');
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update metadata');
    }
  };
  return (
    <Dialog open={!!sticker} onOpenChange={(open) => !open && !mutation.isPending && onClose()}>
      <DialogContent className="max-w-xl" closeDisabled={mutation.isPending}>
        <DialogHeader>
          <DialogTitle>Edit sticker details</DialogTitle>
          <DialogDescription>
            Name and search terms are editable. Artwork and its permanent id stay linked.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="break-all font-mono text-xs text-muted-foreground">{sticker?.stickerId}</p>
          <label className="block space-y-1 text-sm">
            Display name
            <Input value={name} maxLength={128} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block space-y-1 text-sm">
            Search keywords
            <Textarea
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="Comma-separated aliases and visible words"
            />
          </label>
          <label className="block space-y-1 text-sm">
            Content tags
            <Input value={tags} onChange={(e) => setTags(e.target.value)} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1 text-sm">
              Style id
              <Input value={style} onChange={(e) => setStyle(e.target.value)} />
            </label>
            <label className="block space-y-1 text-sm">
              Style label
              <Input value={styleName} onChange={(e) => setStyleName(e.target.value)} />
            </label>
          </div>
          <label className="block space-y-1 text-sm">
            Related search group ids
            <Input value={related} onChange={(e) => setRelated(e.target.value)} />
            <span className="block text-xs text-muted-foreground">
              Search aliases only. Use Groups on the sticker card to choose its groups.
            </span>
          </label>
          <p className="text-xs text-muted-foreground">
            Use Arrange stickers in the group to drag cards into order.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || mutation.isPending} onClick={() => void save()}>
            {mutation.isPending ? 'Saving…' : 'Save details'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
